import { BooleanSelection } from '@src/types/index';
import { isNullish } from '@utils/isNullish';
import { isObject } from '@utils/typeCheck';

import { FormTouched, FormValues } from './types';

const missingFormValues = [null, undefined];
const emptyFormValues: Array<boolean | number | string | Array<string>> = [
  false,
  0,
  '',
  BooleanSelection.No,
];
// These are prefilled values which will never be empty
const ignoreFormKeys = [
  // /your-details
  'aboutPreviousAddress',
  'ddBillingAddressForm',
  'ddBillingAddress',
  'ddBillingSame',
  'rlAcceptMarketing',
];

// The appointment holds all the form values.
// This ensures that only values in the form context are picked up.
const pickFormValues = (
  state: FormValues,
  initialValues: FormValues
): FormValues =>
  Object.keys(initialValues).reduce<FormValues>((newState, key) => {
    const stateValue = state[key];

    if (isNullish(stateValue)) {
      // All address forms has nested states
      // Formik throws runtime exception if initial value is not an object
      return { ...newState, [key]: initialValues[key] };
    }

    return { ...newState, [key]: stateValue };
  }, {});

// The appointment holds all the form values.
// This ensures that only touched values in the form context are picked up.
const getFormTouched = (
  state: FormValues,
  initialValues: FormValues
): FormTouched =>
  Object.keys(initialValues).reduce<FormTouched>((touched, key) => {
    const stateValue = state[key];
    if (
      !stateValue ||
      emptyFormValues.includes(stateValue as string | number | boolean)
    ) {
      return touched;
    }

    if (isObject(stateValue) && isObject(initialValues[key])) {
      return {
        ...touched,
        [key]: getFormTouched(stateValue, initialValues[key]),
      };
    }

    return {
      ...touched,
      [key]: true,
    };
  }, {});

const isEmptyForm = (
  values: FormValues,
  additionalIgnoreKeys: string[] = []
): boolean => {
  const keysToIgnore =
    additionalIgnoreKeys.length > 0
      ? [...ignoreFormKeys, ...additionalIgnoreKeys]
      : ignoreFormKeys;

  let isEmpty = true;
  for (const key of Object.keys(values)) {
    if (!isEmpty) {
      break;
    }

    if (keysToIgnore.includes(key)) {
      continue;
    }

    const formValue = values[key];
    if (isObject(formValue)) {
      isEmpty = isEmptyForm(formValue as FormValues, additionalIgnoreKeys);
    } else {
      isEmpty = [...emptyFormValues, ...missingFormValues].includes(formValue);
    }
  }

  return isEmpty;
};

export { pickFormValues, getFormTouched, isEmptyForm };
